import { useState, useEffect } from 'react';
import axios from 'axios';

// El consecutivo de un papel.
//
// «Quiero colocarle consecutivos a las facturas, a todas, de tal manera que en
// algún momento vaya a buscar una factura, ingreso el consecutivo y la
// encuentro.»
//
// El número NO se calcula aquí: lo reparte el servidor, que es el único que
// puede garantizar que no se repita ni deje huecos. Esto solo lo pide y lo
// guarda mientras el papel está abierto.
//
// ── POR QUÉ ES UN ENGANCHE Y NO UNA FUNCIÓN ──
//
// Las relaciones no existen como registro: se calculan de un periodo. Su número
// hay que pedirlo al abrirlas, y eso es una llamada al servidor — que en React
// solo se puede hacer desde un efecto.
//
// Va SIEMPRE antes de cualquier `return` temprano del componente: React exige
// que los enganches se llamen en el mismo orden en cada pintada, y un papel
// cerrado —con `r` en nulo— saltaría el enganche y rompería ese orden. Por eso
// acepta una llave nula y en ese caso no pide nada.
export function useConsecutivo(serie, llave, fecha) {
  return usePapel(serie, llave, fecha).numero;
}

// El papel entero: su número y la nota que tenga escrita.
//
// `useConsecutivo` sigue devolviendo solo el número, que es lo único que
// necesitan la remisión, el comprobante y la relación de venta. Cambiarle el
// retorno habría obligado a tocar cuatro papeles para que uno tuviera nota.
//
// ── LA NOTA ──
//
// «Gabriel quiere colocarle una nota a la factura, que cuando se imprima se
// vea. Que yo pueda presionar un botón, digite el mensaje que yo desee, y
// cuando no quiera colocar nada pues no hago nada.»
//
// Viaja con el número y no en una llamada aparte: la pantalla ya pregunta por
// el número al abrir el papel, y pedir la nota después dejaría la hoja
// pintándose un instante sin ella.
//
// `guardar` devuelve una promesa para que la pantalla pueda decir si falló. La
// nota se pinta de una vez —antes de que el servidor conteste— porque es lo que
// Gabriel acaba de escribir y ya lo está viendo; si el guardado falla se
// devuelve a lo que había y se avisa, que es peor que no pintarla pero mucho
// mejor que pintar una nota que no quedó guardada.
export function usePapel(serie, llave, fecha) {
  const [numero, setNumero] = useState(null);
  const [nota, setNota] = useState(null);

  useEffect(() => {
    if (!serie || !llave) { setNumero(null); setNota(null); return undefined; }
    let vive = true;
    axios.post('/api/documentos/numero', { serie, llave, fecha })
      .then(r => {
        if (!vive) return;
        setNumero((r.data && r.data.numero) || null);
        setNota((r.data && r.data.nota) || null);
      })
      // Sin número el papel sale igual. Perder una relación por no poder
      // numerarla sería cambiar un adorno por el documento.
      .catch(() => { if (vive) { setNumero(null); setNota(null); } });
    return () => { vive = false; };
  }, [serie, llave, fecha]);

  const guardarNota = (texto) => {
    const limpio = String(texto == null ? '' : texto).trim();
    const antes = nota;
    setNota(limpio || null);
    return axios.put('/api/documentos/nota', { serie, llave, nota: limpio })
      .catch(err => { setNota(antes); throw err; });
  };

  return { numero, nota, guardarNota };
}

// La llave que identifica un papel, que es lo que hace que abrirlo dos veces
// devuelva el MISMO número y no gaste uno nuevo.
//
//   · Los que YA son un registro —una venta, un pago— se identifican por su id.
//   · Las relaciones, por a quién y qué periodo.
export const llaveDeRegistro = (id) => (id ? String(id) : null);
export const llaveDeRelacion = (quien, id, desde, hasta) =>
  (id && desde && hasta ? `${quien}${id}|${desde}|${hasta}` : null);
